import classNames from 'classnames';
import { DetailedHTMLProps, ElementType, FC, HTMLAttributes, ReactNode } from 'react';

import { ColorConfig } from '@/contexts/AntdConfigProvider/theme';

import { TypographyVariants, TypographyVariantsType } from './Typography.types';

import css from './Typography.module.scss';

interface TypographyProps extends DetailedHTMLProps<HTMLAttributes<HTMLParagraphElement>, HTMLParagraphElement> {
  children: ReactNode;
  variant?: TypographyVariantsType;
  bold?: boolean;
  semibold?: boolean;
  extrabold?: boolean;
  normal?: boolean;
  textCenter?: boolean;
  color?: ColorConfig;
  // this propperty is mostly for better semantic HTML inside sections, voiceovers and accessibility
  visuallyHidden?: boolean;
  as?: 'span' | 'p' | 'h1' | 'h2' | 'h3' | 'h4' | 'label' | 'li' | 'div';
  htmlFor?: string;
}

const TYPOGRAPHY_VARIANTS_TO_TAGS: Record<TypographyVariantsType, ElementType> = {
  [TypographyVariants.H1]: 'h1',
  [TypographyVariants.H2]: 'h2',
  [TypographyVariants.H3]: 'h3',
  [TypographyVariants.H4]: 'h4',
  [TypographyVariants.TEXT16]: 'p',
  [TypographyVariants.TEXT14]: 'span',
  [TypographyVariants.TEXT10]: 'span',
};

export const Typography: FC<TypographyProps> = ({
  children,
  variant = TypographyVariants.TEXT16,
  className = '',
  bold = false,
  semibold = false,
  normal = false,
  textCenter = false,
  visuallyHidden = false,
  color,
  as,
  ...restProps
}) => {
  const Tag = as ?? TYPOGRAPHY_VARIANTS_TO_TAGS[variant];
  return (
    <Tag
      data-testid="typography"
      {...restProps}
      style={color ? { color: `var(--${color})` } : undefined}
      className={classNames(css.typography, css[variant], className, {
        [css.bold]: bold,
        [css.semibold]: semibold,
        [css.normal]: normal,
        [css.textCenter]: textCenter,
        [css.visuallyHidden]: visuallyHidden,
      })}
    >
      {children}
    </Tag>
  );
};
